import {
  FC,
  createContext,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useSdk, useLang } from "@app/shared/lib/hooks";
import { LoadingOverlay } from "@app/shared/ui";

import { Localization } from "./localization";
import { LocalizationStore } from "./localization-store";

export const LocContext = createContext<Localization>({} as Localization);

export const LocProvider: FC<PropsWithChildren> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const sdk = useSdk();
  const locStore = useMemo(
    () => new LocalizationStore(sdk.api, () => setReady(true)),
    [sdk.api],
  );
  const loc = useMemo(() => new Localization(locStore), [locStore]);

  const { lang } = useLang();

  useEffect(() => {
    if (locStore.lang === lang) {
      setReady(true);

      return;
    }

    let cancelled = false;
    setReady(false);

    (async () => {
      await locStore.init(lang);
      if (!cancelled) setReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [lang, locStore]);

  if (!ready) return <LoadingOverlay position="absolute" />;

  return <LocContext.Provider value={loc}>{children}</LocContext.Provider>;
};
